import { useLocales } from '@shared/locales'
import { DEFAULT_DATE_FORMAT } from '@shared/utils'
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { productNomenclator } from './productNomenclator'
import { ActionsType, PaymentTerm, Product, ProductFormat } from './types'

export const AddEditProduct = ({
  product,
  onSuccess,
  ...props
}: ActionsType) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const formatOptions: { value: ProductFormat; label: string }[] = [
    { value: 'billboard', label: t('products_trendsetter.format.billboard') },
    { value: 'citylight', label: t('products_trendsetter.format.citylight') },
    { value: 'prismatron', label: t('products_trendsetter.format.prismatron') },
    { value: 'perete', label: t('products_trendsetter.format.perete') },
    {
      value: 'billboard_nestandart',
      label: t('products_trendsetter.format.billboard_nestandart')
    },
    { value: 'console', label: t('products_trendsetter.format.console') },
    { value: 'ceasuri', label: t('products_trendsetter.format.ceasuri') }
  ]

  const paymentTermOptions: { value: PaymentTerm; label: string }[] = [
    {
      value: 'avans',
      label: t('products_trendsetter.payment_terms.avans')
    },
    {
      value: 'postfactum',
      label: t('products_trendsetter.payment_terms.postfactum')
    }
  ]

  // Nomenclator options for product selection
  const nomenclatorOptions = productNomenclator.map((item, index) => ({
    value: index,
    label: `${t(`products_trendsetter.format.${item.format}`)} - ${
      item.location_name
    }`
  }))

  const handleNomenclatorSelect = (index: number) => {
    const selectedProduct = productNomenclator[index]
    if (selectedProduct) {
      form.setFieldsValue({
        format: selectedProduct.format,
        location_name: selectedProduct.location_name,
        address: selectedProduct.address,
        internal_code: selectedProduct.internal_code,
        gps_coordinates: selectedProduct.gps_coordinates,
        net_cost: selectedProduct.net_cost,
        gross_cost: selectedProduct.gross_cost,
        discount: selectedProduct.discount,
        additional_costs_tipar: selectedProduct.additional_costs?.tipar,
        additional_costs_montaj: selectedProduct.additional_costs?.montaj,
        additional_costs_demontaj: selectedProduct.additional_costs?.demontaj,
        supplier_name: selectedProduct.supplier_name,
        payment_terms: selectedProduct.payment_terms,
        technical_policies_file_dimensions:
          selectedProduct.technical_policies?.file_dimensions,
        technical_policies_delivery_time:
          selectedProduct.technical_policies?.delivery_time,
        technical_policies_limitations:
          selectedProduct.technical_policies?.limitations
      })
    }
  }

  useEffect(() => {
    if (product) {
      form.setFieldsValue({
        format: product.format,
        location_name: product.location_name,
        address: product.address,
        internal_code: product.internal_code,
        gps_coordinates: product.gps_coordinates,
        start_date: product.start_date ? dayjs(product.start_date) : undefined,
        end_date: product.end_date ? dayjs(product.end_date) : undefined,
        net_cost: product.net_cost,
        gross_cost: product.gross_cost,
        discount: product.discount,
        additional_costs_tipar: product.additional_costs?.tipar,
        additional_costs_montaj: product.additional_costs?.montaj,
        additional_costs_demontaj: product.additional_costs?.demontaj,
        supplier_name: product.supplier_name,
        payment_terms: product.payment_terms,
        technical_policies_file_dimensions:
          product.technical_policies?.file_dimensions,
        technical_policies_delivery_time:
          product.technical_policies?.delivery_time,
        technical_policies_limitations: product.technical_policies?.limitations
      })
    } else {
      form.resetFields()
    }
  }, [product, form])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      // Demo: Simulate API call
      setTimeout(() => {
        setLoading(false)
        const { nomenclator_product: _nomenclator_product, ...productValues } =
          values
        const newProduct: Product = {
          id: product?.id || Date.now(),
          format: productValues.format,
          location_name: productValues.location_name,
          address: productValues.address,
          internal_code: productValues.internal_code,
          gps_coordinates: productValues.gps_coordinates,
          start_date: productValues.start_date
            ? productValues.start_date.format('YYYY-MM-DD')
            : new Date().toISOString().split('T')[0],
          end_date: productValues.end_date
            ? productValues.end_date.format('YYYY-MM-DD')
            : new Date().toISOString().split('T')[0],
          net_cost: productValues.net_cost || 0,
          gross_cost: productValues.gross_cost || 0,
          discount: productValues.discount,
          additional_costs: {
            tipar: productValues.additional_costs_tipar,
            montaj: productValues.additional_costs_montaj,
            demontaj: productValues.additional_costs_demontaj
          },
          supplier_name: productValues.supplier_name,
          payment_terms: productValues.payment_terms,
          technical_policies: {
            file_dimensions: productValues.technical_policies_file_dimensions,
            delivery_time: productValues.technical_policies_delivery_time,
            limitations: productValues.technical_policies_limitations
          },
          images: product?.images || [],
          created_at: product?.created_at || new Date().toISOString(),
          modified_at: new Date().toISOString()
        }

        onSuccess?.([newProduct])
        props.onCancel?.()
      }, 500)
    } catch {
      // Form validation failed
    }
  }

  return (
    <Modal
      width={900}
      maskClosable={false}
      title={
        product
          ? t('products_trendsetter.edit_product')
          : t('products_trendsetter.add_product')
      }
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
      onOk={handleOk}
      confirmLoading={loading}
      {...props}
    >
      <Form form={form} layout="vertical">
        {/* Nomenclator Selection - only when adding new product */}
        {!product && (
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="nomenclator_product"
                label={t('products_trendsetter.select_from_nomenclator')}
              >
                <Select
                  placeholder={t(
                    'products_trendsetter.nomenclator_placeholder'
                  )}
                  options={nomenclatorOptions}
                  allowClear
                  onChange={handleNomenclatorSelect}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Format Section */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="format"
              label={t('products_trendsetter.format_label')}
              rules={[{ required: true }]}
            >
              <Select
                placeholder={t('products_trendsetter.format_placeholder')}
                options={formatOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Location Section */}
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>
          {t('products_trendsetter.location_section')}
        </h4>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="location_name"
              label={t('products_trendsetter.location_name')}
              rules={[{ required: true }]}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.location_name_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="address"
              label={t('products_trendsetter.address')}
              rules={[{ required: true }]}
            >
              <Input
                placeholder={t('products_trendsetter.address_placeholder')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="internal_code"
              label={t('products_trendsetter.internal_code')}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.internal_code_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="gps_coordinates"
              label={t('products_trendsetter.gps_coordinates')}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.gps_coordinates_placeholder'
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Terms Section */}
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>
          {t('products_trendsetter.terms_section')}
        </h4>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="start_date"
              label={t('products_trendsetter.start_date')}
              rules={[{ required: true }]}
            >
              <DatePicker
                format={DEFAULT_DATE_FORMAT}
                style={{ width: '100%' }}
                placeholder={t('products_trendsetter.start_date_placeholder')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="end_date"
              label={t('products_trendsetter.end_date')}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (
                      !value ||
                      !getFieldValue('start_date') ||
                      dayjs(value).isAfter(dayjs(getFieldValue('start_date')))
                    ) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(t('products_trendsetter.end_date_after_start'))
                    )
                  }
                })
              ]}
            >
              <DatePicker
                format={DEFAULT_DATE_FORMAT}
                style={{ width: '100%' }}
                placeholder={t('products_trendsetter.end_date_placeholder')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Costs Section */}
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>
          {t('products_trendsetter.costs_section')}
        </h4>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="net_cost"
              label={t('products_trendsetter.net_cost')}
              rules={[{ required: true, type: 'number', min: 0 }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder={t('products_trendsetter.net_cost_placeholder')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="gross_cost"
              label={t('products_trendsetter.gross_cost')}
              rules={[{ required: true, type: 'number', min: 0 }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder={t('products_trendsetter.gross_cost_placeholder')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="discount"
              label={t('products_trendsetter.discount')}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                suffix="%"
                placeholder={t('products_trendsetter.discount_placeholder')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="additional_costs_tipar"
              label={t('products_trendsetter.additional_costs.tipar')}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder={t(
                  'products_trendsetter.additional_costs.tipar_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="additional_costs_montaj"
              label={t('products_trendsetter.additional_costs.montaj')}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder={t(
                  'products_trendsetter.additional_costs.montaj_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="additional_costs_demontaj"
              label={t('products_trendsetter.additional_costs.demontaj')}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder={t(
                  'products_trendsetter.additional_costs.demontaj_placeholder'
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Supplier Info Section */}
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>
          {t('products_trendsetter.supplier_section')}
        </h4>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="supplier_name"
              label={t('products_trendsetter.supplier_name')}
              rules={[{ required: true }]}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.supplier_name_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="payment_terms"
              label={t('products_trendsetter.payment_terms_label')}
              rules={[{ required: true }]}
            >
              <Select
                placeholder={t(
                  'products_trendsetter.payment_terms_placeholder'
                )}
                options={paymentTermOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Technical Policies Section */}
        <h4 style={{ marginTop: 16, marginBottom: 8 }}>
          {t('products_trendsetter.technical_policies_section')}
        </h4>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              name="technical_policies_file_dimensions"
              label={t(
                'products_trendsetter.technical_policies.file_dimensions'
              )}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.technical_policies.file_dimensions_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="technical_policies_delivery_time"
              label={t('products_trendsetter.technical_policies.delivery_time')}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.technical_policies.delivery_time_placeholder'
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="technical_policies_limitations"
              label={t('products_trendsetter.technical_policies.limitations')}
            >
              <Input
                placeholder={t(
                  'products_trendsetter.technical_policies.limitations_placeholder'
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
